import * as React from 'react';
import './TeacherTable.css';
import {
  Avatar,
  Box,
  Divider,
  MenuItem,
  Popover,
  Select,
  Typography,
  IconButton,
  Checkbox,
} from '@mui/material';
import { BiSort } from 'react-icons/bi';
import { useState } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OneOnOneSessionIcon from '../../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import { getAccountStatus } from '../../../../../utils/auditActorUtils';
import InstructorTableSkeleton from '../../../../SkeletonEffect/InstructorTableSkeleton/InstructorTableSkeleton';
import dayjs from 'dayjs';

const LevelToTeachColor = {
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'UN',
    value2: 'University',
  },
  high_school: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'HS',
    value2: 'High School',
  },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'MS',
    value2: 'Middle School',
  },
  elementary: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'EL',
    value2: 'Elementary',
  },
};

const CreativeArtsLevelColor = {
  beginner: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'BG',
    value2: 'Beginner',
  },
  intermediate: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'IM',
    value2: 'Intermediate',
  },
  advanced: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'AD',
    value2: 'Advanced',
  },
  professional: {
    color: '#E74C3C',
    bgcolor: '#FFEFED',
    value: 'PR',
    value2: 'Professional',
  },
};

const StatusColor = {
  enable: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Enable' },
  disable: { color: '#949DA6', bgcolor: '#F5F5F5', value: 'Disable' },
};

export default function TeacherTable({
  searchTerm,
  teacherRequestData,
  category,
  value,
  selectedSubject,
  selectedIds = new Set(),
  onSelectOne = () => {},
  onSelectAll = () => {},
  tableType = 'academic', // 'academic' or 'creative-arts'
}) {
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [filteredTeacherRequestData, setFilteredTeacherRequestData] = useState([]);
  const navigate = useNavigate();
  const [loading] = useState(false);

  // Use the filtered data passed from parent instead of making separate API calls
  useEffect(() => {
    if (teacherRequestData && Array.isArray(teacherRequestData)) {
      // Preserve the exact order received from API
      setFilteredTeacherRequestData(teacherRequestData);
    } else {
      setFilteredTeacherRequestData([]);
    }
  }, [teacherRequestData, sortConfig]);

  // Helpers for bulk-select ------------------------------------------------
  const currentPageIds = filteredTeacherRequestData.map(r => r.id);
  const allSelectedOnPage =
    currentPageIds.every(id => selectedIds.has(id)) && currentPageIds.length > 0;
  const someSelectedOnPage = currentPageIds.some(id => selectedIds.has(id)) && !allSelectedOnPage;

  const handleCellClick = (event, id, cellType) => {
    // Ignore clicks originating from a checkbox
    if (event.target.closest('input[type="checkbox"]')) return;
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      if (cellType === 'sessions') {
        // Navigate to the Sessions tab (index 1) in instructor details
        navigate(`/admin/users/instructor-details/${id}?tab=1`);
        // Also store in session storage
        sessionStorage.setItem(`teacherTab-${id}`, '1');
      } else {
        navigate(`/admin/users/instructor-details/${id}`);
      }
    }
  };

  function formatText(input) {
    return input
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
  }

  const PopoverLevels = ({ levels, rowId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl) && levels.length > 0;
    const levelColorMap =
      tableType === 'creative-arts' ? CreativeArtsLevelColor : LevelToTeachColor;

    const handlePopoverOpen = event => {
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    return (
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        className="teacher-level-container"
      >
        <Box className="teacher-level-container">
          {levels.map((level, index) => (
            <Box
              key={index}
              style={{
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 8px',
                borderRadius: '20px',
                backgroundColor: levelColorMap[level]?.bgcolor,
                color: levelColorMap[level]?.color,
                fontFamily: 'Poppins !important',
                fontSize: '14px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: 'normal !important',
              }}
            >
              <Typography className="teacher-level-text">
                {levelColorMap[level]?.value || '---'}
              </Typography>
            </Box>
          ))}
        </Box>

        {levels.length > 0 && (
          <Popover
            id={`level-popover-${rowId}`}
            sx={{
              pointerEvents: 'none',
              '& .MuiPopover-paper': {
                borderRadius: '8px',
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-6px',
                  left: '20px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                },
              },
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
            PaperProps={{
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              sx: {
                pointerEvents: 'auto',
                minWidth: '150px',
              },
            }}
          >
            <Box
              sx={{
                p: 1,
                '& > :not(:last-child)': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {levels.map((level, index) => (
                <Box
                  key={index}
                  style={{
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 8px',
                    borderRadius: '20px',
                    backgroundColor: levelColorMap[level]?.bgcolor,
                    color: levelColorMap[level]?.color,
                    fontFamily: 'Poppins !important',
                    fontSize: '12px !important',
                    fontStyle: 'normal !important',
                    fontWeight: '400 !important',
                    marginBottom: '2px',
                  }}
                >
                  <Typography className="teacher-level-text">
                    {levelColorMap[level]?.value2}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Popover>
        )}
      </div>
    );
  };

  const PopoverSubjects = ({ subjects, rowId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl) && subjects.length > 1;

    const handlePopoverOpen = event => {
      if (subjects.length > 1) {
        setAnchorEl(event.currentTarget);
      }
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    // Extract subject titles for display
    const subjectTitles = subjects.map(subject => subject.title);

    return (
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        className="teacher-subject-container"
      >
        <Box className="teacher-subject-container" sx={{ gap: '6px' }}>
          <Typography className="teacher-subject-text">
            {formatText(
              subjectTitles && subjectTitles.length > 0
                ? subjectTitles.slice(0, 1).join(', ')
                : '---'
            )}
          </Typography>
          {subjects.length > 1 && (
            <Box className="teacher-subject-count-badge">
              <Typography className="teacher-subject-count-text">+{subjects.length - 1}</Typography>
            </Box>
          )}
        </Box>

        {subjects.length > 1 && (
          <Popover
            id={`mouse-over-popover-${rowId}`}
            sx={{
              pointerEvents: 'none',
              '& .MuiPopover-paper': {
                overflow: 'visible',
                borderRadius: '8px',
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-6px',
                  left: '20px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                },
              },
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
            PaperProps={{
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              sx: {
                pointerEvents: 'auto',
                width: '250px',
              },
            }}
          >
            <Box
              sx={{
                p: 1,
                width: '250px',
                '& > :not(:last-child)': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {subjects.map((subject, index) => (
                <Typography
                  key={index}
                  sx={{
                    py: 1,
                    px: 2,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: '4px',
                    },
                  }}
                >
                  {formatText(subject.title)}
                </Typography>
              ))}
            </Box>
          </Popover>
        )}
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <InstructorTableSkeleton />
      ) : (
        <Box className="teacher-table-container">
          {filteredTeacherRequestData.length === 0 ? (
            <table className="teacher-table">
              <thead className="teacher-table-header">
                <tr>
                  <th className="teacher-th teacher-table-checkbox-column">
                    <Checkbox
                      indeterminate={someSelectedOnPage}
                      checked={allSelectedOnPage}
                      onChange={e => onSelectAll(currentPageIds, e.target.checked)}
                      inputProps={{ 'aria-label': 'Select all instructors on current page' }}
                    />
                  </th>
                  <th className="teacher-th teacher-table-name-column">Name</th>
                  <th className="teacher-th teacher-table-experience-column">
                    Years of Experience
                  </th>
                  <th className="teacher-th teacher-table-level-column">Level to Teach</th>
                  <th className="teacher-th teacher-table-subjects-column">Subjects</th>
                  <th className="teacher-th teacher-table-sessions-column">Total Sessions</th>
                  <th className="teacher-th teacher-table-rating-column">Rating</th>
                  <th className="teacher-th teacher-table-session-type-column">Type of session</th>
                  <th className="teacher-th teacher-table-earned-column">Total Earned (Riyals)</th>
                  <th className="teacher-th teacher-table-coupon-column">Coupon Used</th>
                  <th className="teacher-th teacher-table-date-column">Approval Date</th>
                  <th className="teacher-th teacher-table-status-column">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={12} className="teacher-empty-cell">
                    <Box className="teacher-empty-state">
                      <Typography className="teacher-empty-text">
                        No instructors available
                      </Typography>
                    </Box>
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="teacher-table">
              <thead className="teacher-table-header">
                <tr>
                  <th className="teacher-th teacher-table-checkbox-column">
                    <Checkbox
                      indeterminate={someSelectedOnPage}
                      checked={allSelectedOnPage}
                      onChange={e => onSelectAll(currentPageIds, e.target.checked)}
                      inputProps={{ 'aria-label': 'Select all instructors on current page' }}
                    />
                  </th>
                  <th className="teacher-th teacher-table-name-column">Name</th>
                  <th className="teacher-th teacher-table-experience-column">
                    Years of Experience
                  </th>
                  <th className="teacher-th teacher-table-level-column">Level to Teach</th>
                  <th className="teacher-th teacher-table-subjects-column">Subjects</th>
                  <th className="teacher-th teacher-table-sessions-column">Total Sessions</th>
                  <th className="teacher-th teacher-table-rating-column">Rating</th>
                  <th className="teacher-th teacher-table-session-type-column">Type of session</th>
                  <th className="teacher-th teacher-table-earned-column">Total Earned (Riyals)</th>
                  <th className="teacher-th teacher-table-coupon-column">Coupon Used</th>
                  <th className="teacher-th teacher-table-date-column">Approval Date</th>
                  <th className="teacher-th teacher-table-status-column">Status</th>
                </tr>
              </thead>
              <tbody className="teacher-table-body">
                {filteredTeacherRequestData.map(row => {
                  // Treat an account as enabled only when it is active AND no
                  // feature has been individually blocked; any block shows as
                  // "Disable".
                  const isEnabled = getAccountStatus(row) === 'enabled';
                  return (
                    <tr
                      key={row.id}
                      className={!isEnabled ? 'teacher-table-row-disabled' : 'teacher-table-row'}
                    >
                      <td
                        onClick={e => e.stopPropagation()}
                        className="teacher-td teacher-table-checkbox-column"
                      >
                        <Checkbox
                          checked={selectedIds.has(row.id)}
                          onChange={e => onSelectOne(row.id, e.target.checked)}
                          inputProps={{
                            'aria-label': `Select instructor ${row.first_name} ${row.last_name}`,
                          }}
                        />
                      </td>
                      <td
                        className="teacher-td teacher-table-name-column"
                        onClick={e => handleCellClick(e, row.id)}
                      >
                        <Box className="teacher-name-text">
                          <Avatar
                            src={row?.profile_image || '/broken-image.jpg'}
                            className="teacher-avatar"
                          />
                          <Typography className="teacher-name-text">
                            {row.first_name} {row.last_name}
                          </Typography>
                        </Box>
                      </td>
                      <td className="teacher-td teacher-table-experience-column">
                        {row.years_of_experience || '---'}
                      </td>
                      <td className="teacher-td teacher-table-level-column">
                        {Array.isArray(row.level_of_teaching) &&
                        row.level_of_teaching.length > 0 ? (
                          <PopoverLevels
                            levels={row.level_of_teaching}
                            rowId={row.id}
                            tableType={tableType}
                          />
                        ) : (
                          <Typography className="teacher-level-text">---</Typography>
                        )}
                      </td>
                      <td
                        className="teacher-td teacher-table-subjects-column"
                        onClick={e => handleCellClick(e, row.id)}
                      >
                        <Box className="teacher-subject-container">
                          {Array.isArray(row.subjects) && row.subjects.length > 0 ? (
                            <PopoverSubjects subjects={row.subjects} rowId={row.id} />
                          ) : (
                            <Typography className="teacher-subject-text">---</Typography>
                          )}
                        </Box>
                      </td>
                      <td
                        className="teacher-td teacher-table-sessions-column"
                        onClick={e => handleCellClick(e, row.id, 'sessions')}
                      >
                        {row.total_sessions ? row.total_sessions : '0'}
                      </td>
                      <td
                        className="teacher-td teacher-table-rating-column"
                        onClick={e => handleCellClick(e, row.id)}
                      >
                        {row.rating
                          ? `${Number(row.rating) % 1 === 0 ? Number(row.rating) : Number(row.rating).toFixed(1)}/5`
                          : `0/5`}
                      </td>
                      <td
                        className="teacher-td teacher-table-session-type-column"
                        onClick={e => handleCellClick(e, row.id)}
                      >
                        <Box className="teacher-session-type-container">
                          {row?.allow_one_on_one_session && (
                            <img src={OneOnOneSessionIcon} alt="One-On-One" />
                          )}
                          {row?.allow_one_on_one_session && row?.allow_group_session && (
                            <Divider orientation="vertical" flexItem className="teacher-divider" />
                          )}
                          {row?.allow_group_session && <img src={GroupSessionIcon} alt="Group" />}
                          {row?.allow_group_session && row?.allow_prime_session && (
                            <Divider orientation="vertical" flexItem className="teacher-divider" />
                          )}
                          {row?.allow_prime_session && <img src={PrimeSessionIcon} alt="Prime" />}
                        </Box>
                      </td>
                      <td
                        className="teacher-td teacher-table-earned-column"
                        onClick={e => handleCellClick(e, row.id)}
                      >
                        {row?.total_earnings
                          ? parseFloat(row.total_earnings * 7.5).toFixed(2)
                          : '0'}
                      </td>
                      <td className="teacher-td teacher-table-coupon-column">
                        <Box className="teacher-coupon-container">
                          <Typography className="teacher-coupon-text">
                            {row?.used_coupon_code ? row?.used_coupon_code : 'No coupon used'}
                          </Typography>
                        </Box>
                      </td>
                      <td className="teacher-td teacher-table-date-column">
                        {row?.approved_at ? dayjs(row?.approved_at).format('DD-MM-YYYY') : '---'}
                      </td>
                      <td
                        className="teacher-td teacher-table-status-column"
                        onClick={e => handleCellClick(e, row.id)}
                      >
                        <Box
                          className="teacher-status-container"
                          style={{
                            border: `1px solid ${
                              isEnabled
                                ? StatusColor['enable']?.color
                                : StatusColor['disable']?.color
                            }`,
                            backgroundColor: isEnabled
                              ? StatusColor['enable']?.bgcolor
                              : StatusColor['disable']?.bgcolor,
                            color: isEnabled
                              ? StatusColor['enable']?.color
                              : StatusColor['disable']?.color,
                          }}
                        >
                          <Typography
                            className="teacher-status-dot"
                            style={{
                              backgroundColor: isEnabled
                                ? StatusColor['enable']?.color
                                : StatusColor['disable']?.color,
                            }}
                          ></Typography>
                          <Typography className="teacher-status-text">
                            {isEnabled ? 'Enable' : 'Disable'}
                          </Typography>
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Box>
      )}
    </>
  );
}
